// Zustand store for app lock state and preferences

import { create } from 'zustand';

const STORAGE_KEY = 'nomi-lock-preferences';

interface LockPreferences {
  idleTimeoutMinutes: number;
  lockOnHide: boolean;
  lockOnHideGraceSeconds: number;
}

interface LockState {
  isLocked: boolean;
  pinEnabled: boolean;
  pinStatusChecked: boolean;
  lastActivityAt: number;
  failedAttempts: number;
  preferences: LockPreferences;

  lock: () => void;
  unlock: () => void;
  setPinEnabled: (enabled: boolean, lockImmediately?: boolean) => void;
  markPinStatusChecked: () => void;
  recordActivity: () => void;
  incrementFailedAttempts: () => number;
  resetFailedAttempts: () => void;
  setPreferences: (prefs: Partial<LockPreferences>) => void;
}

function loadPreferences(): LockPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultPreferences, ...JSON.parse(stored) };
    }
  } catch {
    // ignore parse errors
  }
  return defaultPreferences;
}

function savePreferences(prefs: LockPreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

const defaultPreferences: LockPreferences = {
  idleTimeoutMinutes: 5,
  lockOnHide: true,
  lockOnHideGraceSeconds: 30,
};

export const useLockStore = create<LockState>((set, get) => ({
  isLocked: false,
  pinEnabled: false,
  pinStatusChecked: false,
  lastActivityAt: Date.now(),
  failedAttempts: 0,
  preferences: loadPreferences(),

  lock: () => set({ isLocked: true, failedAttempts: 0 }),

  unlock: () => set({ isLocked: false, failedAttempts: 0, lastActivityAt: Date.now() }),

  setPinEnabled: (enabled, lockImmediately = true) => {
    set({ pinEnabled: enabled, isLocked: enabled && lockImmediately, pinStatusChecked: true });
  },

  markPinStatusChecked: () => set({ pinStatusChecked: true }),

  recordActivity: () => set({ lastActivityAt: Date.now() }),

  incrementFailedAttempts: () => {
    const next = get().failedAttempts + 1;
    set({ failedAttempts: next });
    return next;
  },

  resetFailedAttempts: () => set({ failedAttempts: 0 }),

  setPreferences: (prefs) => {
    const updated = { ...get().preferences, ...prefs };
    savePreferences(updated);
    set({ preferences: updated });
  },
}));
